import { Fragment, useEffect, useState } from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { ChevronDown, Menu, X } from "lucide-react"
import GiselaNavDropdown from "@/components/gisela/GiselaNavDropdown"
import {
  LANGUAGE_NAMES,
  localeSwitchTarget,
  otherLocale,
  rememberLanguageChoice,
  useLocalePath,
} from "@/lib/locale-path"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"

interface Props {
  /** The page under it opens on a dark, full-bleed hero (her home page since
   *  the hero moved onto a photographic ground). The bar then rides on the
   *  photo in white and only paints itself cream once the visitor scrolls
   *  past — PublicNav's behaviour on the venue's own heroes. */
  overHero?: boolean
}

// Nav for Gisela's site. Solid cream from the start on every page that opens
// on a light background, which is all of them but the home page.
export default function GiselaNav({ overHero = false }: Props) {
  const { url, props } = usePage<PageProps>()
  const { current_locale, translations, brand_urls, current_brand } = props
  const t = translations.gisela_nav
  const phone = current_brand.phone
  const lp = useLocalePath()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [meetOpen, setMeetOpen] = useState(false)
  const [therapiesOpen, setTherapiesOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  // On the photo, or on the cream bar. Opening the burger panel counts as
  // off the photo: the panel is cream, and a transparent bar above it would
  // leave white links on a cream sheet.
  const onPhoto = overHero && !scrolled && !mobileOpen

  const conditionMenu = props.gisela_condition_menu ?? []
  const therapyMenu = props.gisela_therapy_menu ?? []

  // Pages the admin has switched off drop out of every menu — filter on the
  // raw English path, before lp() localizes it.
  const hidden = props.hidden_paths
  const showHelpWith = !hidden.includes("/what-i-can-help-with")
  const showTherapies = !hidden.includes("/therapies-i-offer")
  const showBook = !hidden.includes("/book")
  // Online therapy, then her page per town — how and where a client meets
  // her, one menu. The label still opens the online page: that is most of
  // her work, and the town pages are the items under it.
  const meetMenu = (props.gisela_meet_menu ?? []).map((group) => ({
    ...group,
    items: group.items.filter((item) => !hidden.includes(item.path)),
  }))
  const showMeet = meetMenu.some((group) => group.items.length > 0)

  // "What I help with" and "Therapies I offer" are rendered separately, as
  // the two dropdowns. The standalone OCD link is gone from the row on
  // purpose: OCD is the conditions menu's first item (and still in the
  // footer, hero and burger panel), and dropping it bought back the width
  // the therapies tab now spends in a row already at capacity at 1280px.
  // The Sanctuary link is squeezed out below 2xl — burger menu and footer only.
  const pageLinks: { label: string; path: string }[] = [
    { label: t.about,          path: "/about" },
    { label: t.blog,           path: "/blog" },
    { label: t.fees,           path: "/fees" },
    { label: t.contact,        path: "/contact" },
  ]
  const navLinks: { label: string; href: string }[] = pageLinks
    .filter(({ path }) => !hidden.includes(path))
    .map(({ label, path }) => ({ label, href: lp(path) }))
  // The venue is not a page of hers: the row leaves it out (the footer
  // carries its logo and link on every page), the burger menu lists it.
  const menuLinks = [ ...navLinks, { label: t.sanctuary, href: brand_urls.sanctuary } ]

  useEffect(() => {
    if (!overHero) return
    const onScroll = () => setScrolled(window.scrollY > 40)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [overHero])

  useEffect(() => {
    setMobileOpen(false)
    setHelpOpen(false)
    setTherapiesOpen(false)
    setMeetOpen(false)
  }, [url])

  const switchLocale = (locale: string) => {
    rememberLanguageChoice(locale)
    const { path, query } = localeSwitchTarget(locale, window.location.pathname, props.locale_slugs)
    router.get(path, query, {
      preserveScroll: true,
      preserveState: false,
    })
  }

  // One button naming the language it switches to, in that language — the
  // person who needs it is the one who cannot read the label beside it, and
  // "Español" is recognised where "ES" has to be decoded. Which language the
  // page is already in needs no badge: the page says so.
  const localeToggle = (className?: string) => {
    const target = otherLocale(current_locale)
    return (
      <button
        lang={target}
        onClick={() => switchLocale(target)}
        className={cn(
          "rounded-full px-2.5 py-1 font-medium transition-colors",
          onPhoto
            ? "text-white/80 hover:bg-white/15 hover:text-white"
            : "text-ink-muted hover:bg-surface hover:text-ink-body",
          className
        )}
      >
        {LANGUAGE_NAMES[target]}
      </button>
    )
  }

  return (
    <nav
      className={cn(
        "fixed top-(--admin-bar-h) left-0 right-0 z-50 transition-colors duration-300",
        onPhoto ? "bg-transparent" : "border-b border-hairline bg-cream/95 backdrop-blur-sm"
      )}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-6">
          {/* House mark + wordmark. The Sanctuary symbol (the mark half of the
              official logo, already cut out with transparency) rides to the
              left as a parent-brand cue — she practises at the centre. The
              Sanctuary *wordmark* deliberately never appears here: her name has
              to stay the identity, or visitors can't tell which of the two
              sites they're on. The image is aria-hidden because the adjacent
              text already names the link. */}
          <Link href={lp("/")} className="flex shrink-0 items-center gap-2.5 no-underline">
            <img src="/sanctuary-favicon.png" alt="" aria-hidden className="h-8 w-8 shrink-0" />
            <span className="flex flex-col leading-tight">
              <span className={cn("whitespace-nowrap font-display text-base", onPhoto ? "text-white" : "text-ink-display")}>
                Gisela Ramirez
              </span>
              <span className={cn("whitespace-nowrap text-[11px] tracking-wide", onPhoto ? "text-white/70" : "text-ink-muted")}>
                {t.tagline}
              </span>
            </span>
          </Link>

          {/* Nav links — the row only fits from xl up; below that the burger
              menu takes over (measured: seven links + wordmark + actions
              overflow a 1024px viewport). */}
          <div className="hidden items-center gap-4 xl:flex 2xl:gap-6">
            {showHelpWith && (
              <GiselaNavDropdown
                label={t.help_with}
                href="/what-i-can-help-with"
                menu={conditionMenu}
                triggerClassName={onPhoto ? "text-white" : undefined}
                chevronClassName={onPhoto ? "text-white/70" : undefined}
              />
            )}
            {showTherapies && (
              <GiselaNavDropdown
                label={t.therapies}
                href="/therapies-i-offer"
                menu={therapyMenu}
                browseLabel={t.browse_therapies}
                triggerClassName={onPhoto ? "text-white" : undefined}
                chevronClassName={onPhoto ? "text-white/70" : undefined}
              />
            )}
            {showMeet && (
              <GiselaNavDropdown
                label={t.where_we_meet}
                href="/where-we-meet"
                menu={meetMenu}
                browseLabel={t.browse_where_we_meet}
                triggerClassName={onPhoto ? "text-white" : undefined}
                chevronClassName={onPhoto ? "text-white/70" : undefined}
              />
            )}
            {navLinks.map(({ label, href }) => (
              <a
                key={label}
                href={href}
                className={cn(
                  "whitespace-nowrap text-sm no-underline transition-colors hover:opacity-70",
                  onPhoto ? "text-white" : "text-ink-body"
                )}
              >
                {label}
              </a>
            ))}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            <div className="hidden text-xs xl:block">{localeToggle()}</div>

            {showBook && (
              <a
                href={lp("/book")}
                className={cn(
                  "whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium no-underline transition-opacity hover:opacity-90",
                  onPhoto ? "bg-white text-teal" : "bg-teal text-white"
                )}
              >
                <span className="2xl:hidden">{t.book_call_short}</span>
                <span className="hidden 2xl:inline">{t.book_call}</span>
              </a>
            )}

            <button
              type="button"
              onClick={() => setMobileOpen((open) => !open)}
              aria-expanded={mobileOpen}
              aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
              className={cn(
                "inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors xl:hidden",
                onPhoto ? "text-white hover:bg-white/10" : "text-ink-body hover:bg-surface"
              )}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="border-t border-hairline bg-cream shadow-lg xl:hidden">
          <div className="flex flex-col gap-1 px-6 py-4">
            {/* The conditions menu, as an expandable section: the label still
                navigates, the chevron opens the list. */}
            {showHelpWith && (
            <div className="flex items-center gap-1">
              <a
                href={lp("/what-i-can-help-with")}
                className="flex-1 rounded-md px-2 py-2.5 text-sm text-ink-body no-underline transition-colors hover:bg-surface"
              >
                {t.help_with}
              </a>
              {conditionMenu.length > 0 && (
                <button
                  type="button"
                  onClick={() => setHelpOpen((open) => !open)}
                  aria-expanded={helpOpen}
                  aria-label={t.browse_help_with}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface hover:text-ink-body"
                >
                  <ChevronDown className={cn("h-4 w-4 transition-transform", helpOpen && "rotate-180")} />
                </button>
              )}
            </div>
            )}
            {helpOpen && (
              <div className="mb-1 flex flex-col gap-0.5 border-l border-hairline pl-1">
                {conditionMenu.map((group) => (
                  <Fragment key={group.label}>
                    {group.flyout && (
                      <p className="px-2 pt-2 text-xs uppercase tracking-widest text-ink-muted">
                        {group.label}
                      </p>
                    )}
                    {group.items.map((item) => (
                      <a
                        key={item.path}
                        href={lp(item.path)}
                        className={cn(
                          "rounded-md px-2 py-2 text-sm text-ink-body no-underline transition-colors hover:bg-surface",
                          group.flyout && "pl-5",
                        )}
                      >
                        {item.label}
                      </a>
                    ))}
                  </Fragment>
                ))}
              </div>
            )}
            {/* The therapies menu, the same expandable pattern. It has one
                flat group, so there are no flyout headings to render. */}
            {showTherapies && (
            <div className="flex items-center gap-1">
              <a
                href={lp("/therapies-i-offer")}
                className="flex-1 rounded-md px-2 py-2.5 text-sm text-ink-body no-underline transition-colors hover:bg-surface"
              >
                {t.therapies}
              </a>
              {therapyMenu.length > 0 && (
                <button
                  type="button"
                  onClick={() => setTherapiesOpen((open) => !open)}
                  aria-expanded={therapiesOpen}
                  aria-label={t.browse_therapies}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface hover:text-ink-body"
                >
                  <ChevronDown className={cn("h-4 w-4 transition-transform", therapiesOpen && "rotate-180")} />
                </button>
              )}
            </div>
            )}
            {therapiesOpen && (
              <div className="mb-1 flex flex-col gap-0.5 border-l border-hairline pl-1">
                {therapyMenu.flatMap((group) => group.items).map((item) => (
                  <a
                    key={item.path}
                    href={lp(item.path)}
                    className="rounded-md px-2 py-2 text-sm text-ink-body no-underline transition-colors hover:bg-surface"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            )}
            {/* Where we meet — online, then each town — the same expandable pattern. */}
            {showMeet && (
            <div className="flex items-center gap-1">
              <a
                href={lp("/where-we-meet")}
                className="flex-1 rounded-md px-2 py-2.5 text-sm text-ink-body no-underline transition-colors hover:bg-surface"
              >
                {t.where_we_meet}
              </a>
              <button
                type="button"
                onClick={() => setMeetOpen((open) => !open)}
                aria-expanded={meetOpen}
                aria-label={t.browse_where_we_meet}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface hover:text-ink-body"
              >
                <ChevronDown className={cn("h-4 w-4 transition-transform", meetOpen && "rotate-180")} />
              </button>
            </div>
            )}
            {meetOpen && (
              <div className="mb-1 flex flex-col gap-0.5 border-l border-hairline pl-1">
                {meetMenu.flatMap((group) => group.items).map((item) => (
                  <a
                    key={item.path}
                    href={lp(item.path)}
                    className="rounded-md px-2 py-2 text-sm text-ink-body no-underline transition-colors hover:bg-surface"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            )}
            {menuLinks.map(({ label, href }) => (
              <a
                key={label}
                href={href}
                className="rounded-md px-2 py-2.5 text-sm text-ink-body no-underline transition-colors hover:bg-surface"
              >
                {label}
              </a>
            ))}
          </div>
          <div className="flex items-center justify-between border-t border-hairline px-6 py-3">
            <div className="text-xs">{localeToggle("-ml-2.5")}</div>
            {phone && (
              <a href={`tel:${phone.replace(/\s/g, "")}`} className="text-sm font-medium text-teal no-underline">
                {t.ring_now} · {phone}
              </a>
            )}
          </div>
        </div>
      )}
    </nav>
  )
}
